/**
 * pages.config.js - Page routing configuration
 *
 * PERFORMANCE (code splitting): only the homepage (Forside) is eagerly
 * imported — it must paint instantly. Every other page is lazy-loaded as its
 * own chunk, keeping the initial JS bundle small (PageSpeed flagged ~1.2 MB
 * of unused JS when all pages shipped in one bundle). Navigation shows the
 * global PageLoader for the brief chunk fetch.
 *
 * THE ONLY OTHER EDITABLE VALUE: mainPage — must match a key in PAGES.
 */
import { lazy } from 'react';
import { effectiveEntryPath as entryPath } from '@/components/entryLanguage';
// Warm only the language that will actually render, alongside the auth check.
const homeEntry = ['/', '/forside', '/home'].includes(entryPath) ? import('@/pages/Forside') : null;
const danishEntry = !entryPath.startsWith('/en/') && !['/en', '/about-en'].includes(entryPath) ? import('@/Layout') : null;
const Forside = lazy(() => homeEntry || import('@/pages/Forside'));
const __Layout = lazy(() => danishEntry || import('@/Layout'));

const aboutEn = lazy(() => import('./pages/About-en'));
const About = lazy(() => import('./pages/About'));
const Affiliate = lazy(() => import('./pages/Affiliate'));
const Articles = lazy(() => import('./pages/Articles'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogArtikelBedsteLadestandere = lazy(() => import('./pages/BlogArtikelBedsteLadestandere'));
const BlogArtikelElbiilTest = lazy(() => import('./pages/BlogArtikelElbiilTest'));
const BlogArtikelGuideElLader = lazy(() => import('./pages/BlogArtikelGuideElLader'));
const BlogArtikelImpactStudy = lazy(() => import('./pages/BlogArtikelImpactStudy'));
const BlogArtikelInstallationPris = lazy(() => import('./pages/BlogArtikelInstallationPris'));
const BlogArtikelLadeboks = lazy(() => import('./pages/BlogArtikelLadeboks'));
const BlogArtikelLadekrig = lazy(() => import('./pages/BlogArtikelLadekrig'));
const BlogArtikelLadestandere = lazy(() => import('./pages/BlogArtikelLadestandere'));
const BlogArtikelRefusion = lazy(() => import('./pages/BlogArtikelRefusion'));
const BlogArtikelSammenligning = lazy(() => import('./pages/BlogArtikelSammenligning'));
const BlogArtikelSmartOpladning = lazy(() => import('./pages/BlogArtikelSmartOpladning'));
const BlogArtikelTaenkTest = lazy(() => import('./pages/BlogArtikelTaenkTest'));
const BlogArtikler = lazy(() => import('./pages/BlogArtikler'));
const BlogVideoer = lazy(() => import('./pages/BlogVideoer'));
const Blogbestitestnicolaskj = lazy(() => import('./pages/Blogbestitestnicolaskj'));
const Charger = lazy(() => import('./pages/Charger'));
const checkDinElregning = lazy(() => import('./pages/Check-din-elregning'));
const cookiePolicy = lazy(() => import('./pages/Cookie-policy'));
const Cookiepolitik = lazy(() => import('./pages/Cookiepolitik'));
const easeeUp = lazy(() => import('./pages/Easee-up'));
const elaftaleB = lazy(() => import('./pages/Elaftale-b'));
const elaftaleKvartalsafregning = lazy(() => import('./pages/Elaftale-kvartalsafregning'));
const Elaftale = lazy(() => import('./pages/Elaftale'));
const Elbil = lazy(() => import('./pages/Elbil'));
const elprisSammenligning = lazy(() => import('./pages/Elpris-sammenligning'));
const enkelApp = lazy(() => import('./pages/Enkel-app'));
const erEnkelKlarTil15MinuttersAfregning = lazy(() => import('./pages/Er-enkel-klar-til-15-minutters-afregning'));
const FAQ = lazy(() => import('./pages/FAQ'));
const Fees = lazy(() => import('./pages/Fees'));
const forstaaDinElregning = lazy(() => import('./pages/Forstaa-din-elregning'));
const Gebyrer = lazy(() => import('./pages/Gebyrer'));
const Handelsbetingelser = lazy(() => import('./pages/Handelsbetingelser'));
const Home = lazy(() => import('./pages/Home'));
const hvadKosterElGennemEnkel = lazy(() => import('./pages/Hvad-koster-el-gennem-enkel'));
const hvadKosterInstallationAfLadeboks = lazy(() => import('./pages/Hvad-koster-installation-af-ladeboks'));
const hvilkenLadeboksSkalJegVaelge = lazy(() => import('./pages/Hvilken-ladeboks-skal-jeg-vaelge'));
const hvorLangTidTagerDetAtSkifte = lazy(() => import('./pages/Hvor-lang-tid-tager-det-at-skifte'));
const kanAlleFaaStroemFraEnkel = lazy(() => import('./pages/Kan-alle-faa-stroem-fra-enkel'));
const kanJegKoebeLadeboksUdenElAftale = lazy(() => import('./pages/Kan-jeg-koebe-ladeboks-uden-el-aftale'));
const Kontakt = lazy(() => import('./pages/Kontakt'));
const Kundeportal = lazy(() => import('./pages/Kundeportal'));
const Laderabat = lazy(() => import('./pages/Laderabat'));
const Leveringsbetingelser = lazy(() => import('./pages/Leveringsbetingelser'));
const Loesning = lazy(() => import('./pages/Loesning'));
const Loesning2 = lazy(() => import('./pages/Loesning2'));
const nexblueEdge2 = lazy(() => import('./pages/Nexblue-edge-2'));
const nexblueZenStromsensor = lazy(() => import('./pages/Nexblue-zen-stromsensor'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Opladere = lazy(() => import('./pages/Opladere'));
const Pioneers = lazy(() => import('./pages/Pioneers'));
const Prices = lazy(() => import('./pages/Prices'));
const prisTimeForTime = lazy(() => import('./pages/Pris-time-for-time'));
const prisWidgets = lazy(() => import('./pages/Pris-widgets'));
const privacyPolicy = lazy(() => import('./pages/Privacy-policy'));
const Privatlivspolitik = lazy(() => import('./pages/Privatlivspolitik'));
const Referral = lazy(() => import('./pages/Referral'));
const saadanFungererElMarkedet = lazy(() => import('./pages/Saadan-fungerer-el-markedet'));
const SammenlignHub = lazy(() => import('./pages/SammenlignHub'));
const SammenlignPage = lazy(() => import('./pages/SammenlignPage'));
const Selvbetjening = lazy(() => import('./pages/Selvbetjening'));
const serviceAftale = lazy(() => import('./pages/Service-aftale'));
const Sitemap = lazy(() => import('./pages/Sitemap'));
const skalJegOpsigeHosMitNuvaerendeElselskab = lazy(() => import('./pages/Skal-jeg-opsige-hos-mit-nuvaerende-elselskab'));
const smartCharge = lazy(() => import('./pages/Smart-charge'));
const Solar = lazy(() => import('./pages/Solar'));
const Solcelleaftale = lazy(() => import('./pages/Solcelleaftale'));
const Startside = lazy(() => import('./pages/Startside'));
const Supportkundeportal = lazy(() => import('./pages/Supportkundeportal'));
const Terms = lazy(() => import('./pages/Terms'));
const virkerEnkelMedAlleLadebokse = lazy(() => import('./pages/Virker-enkel-med-alle-ladebokse'));
const Widget = lazy(() => import('./pages/Widget'));
const zaptecGo2 = lazy(() => import('./pages/Zaptec-go-2'));
const zaptecGo = lazy(() => import('./pages/Zaptec-go'));

// Heavy internal/admin tools — lazy-loaded, kept out of the public bundle
const Internalanalytics = lazy(() => import('./pages/Internalanalytics'));
const csDashboard = lazy(() => import('./pages/Cs-dashboard'));
const dealbuilderInternal = lazy(() => import('./pages/Dealbuilder-internal'));
const mediaCreator = lazy(() => import('./pages/Media-creator'));
const SEOGenerator = lazy(() => import('./pages/SEOGenerator'));
const TPRS = lazy(() => import('./pages/TPRS'));
const Upload = lazy(() => import('./pages/Upload'));

export const PAGES = {
    "About-en": aboutEn,
    "About": About,
    "Affiliate": Affiliate,
    "Articles": Articles,
    "Blog": Blog,
    "BlogArtikelBedsteLadestandere": BlogArtikelBedsteLadestandere,
    "BlogArtikelElbiilTest": BlogArtikelElbiilTest,
    "BlogArtikelGuideElLader": BlogArtikelGuideElLader,
    "BlogArtikelImpactStudy": BlogArtikelImpactStudy,
    "BlogArtikelInstallationPris": BlogArtikelInstallationPris,
    "BlogArtikelLadeboks": BlogArtikelLadeboks,
    "BlogArtikelLadekrig": BlogArtikelLadekrig,
    "BlogArtikelLadestandere": BlogArtikelLadestandere,
    "BlogArtikelRefusion": BlogArtikelRefusion,
    "BlogArtikelSammenligning": BlogArtikelSammenligning,
    "BlogArtikelSmartOpladning": BlogArtikelSmartOpladning,
    "BlogArtikelTaenkTest": BlogArtikelTaenkTest,
    "BlogArtikler": BlogArtikler,
    "BlogVideoer": BlogVideoer,
    "Blogbestitestnicolaskj": Blogbestitestnicolaskj,
    "Charger": Charger,
    "Check-din-elregning": checkDinElregning,
    "Cookie-policy": cookiePolicy,
    "Cookiepolitik": Cookiepolitik,
    "Cs-dashboard": csDashboard,
    "Dealbuilder-internal": dealbuilderInternal,
    "Easee-up": easeeUp,
    "Elaftale-b": elaftaleB,
    "Elaftale-kvartalsafregning": elaftaleKvartalsafregning,
    "Elaftale": Elaftale,
    "Elbil": Elbil,
    "Elpris-sammenligning": elprisSammenligning,
    "Enkel-app": enkelApp,
    "Er-enkel-klar-til-15-minutters-afregning": erEnkelKlarTil15MinuttersAfregning,
    "FAQ": FAQ,
    "Fees": Fees,
    "Forside": Forside,
    "Forstaa-din-elregning": forstaaDinElregning,
    "Gebyrer": Gebyrer,
    "Handelsbetingelser": Handelsbetingelser,
    "Home": Home,
    "Hvad-koster-el-gennem-enkel": hvadKosterElGennemEnkel,
    "Hvad-koster-installation-af-ladeboks": hvadKosterInstallationAfLadeboks,
    "Hvilken-ladeboks-skal-jeg-vaelge": hvilkenLadeboksSkalJegVaelge,
    "Hvor-lang-tid-tager-det-at-skifte": hvorLangTidTagerDetAtSkifte,
    "Internalanalytics": Internalanalytics,
    "Kan-alle-faa-stroem-fra-enkel": kanAlleFaaStroemFraEnkel,
    "Kan-jeg-koebe-ladeboks-uden-el-aftale": kanJegKoebeLadeboksUdenElAftale,
    "Kontakt": Kontakt,
    "Kundeportal": Kundeportal,
    "Laderabat": Laderabat,
    "Leveringsbetingelser": Leveringsbetingelser,
    "Loesning": Loesning,
    "Loesning2": Loesning2,
    "Media-creator": mediaCreator,
    "Nexblue-edge-2": nexblueEdge2,
    "Nexblue-zen-stromsensor": nexblueZenStromsensor,
    "NotFound": NotFound,
    "Opladere": Opladere,
    "Pioneers": Pioneers,
    "Prices": Prices,
    "Pris-time-for-time": prisTimeForTime,
    "Pris-widgets": prisWidgets,
    "Privacy-policy": privacyPolicy,
    "Privatlivspolitik": Privatlivspolitik,
    "Referral": Referral,
    "SEOGenerator": SEOGenerator,
    "Saadan-fungerer-el-markedet": saadanFungererElMarkedet,
    "SammenlignHub": SammenlignHub,
    "SammenlignPage": SammenlignPage,
    "Selvbetjening": Selvbetjening,
    "Service-aftale": serviceAftale,
    "Sitemap": Sitemap,
    "Skal-jeg-opsige-hos-mit-nuvaerende-elselskab": skalJegOpsigeHosMitNuvaerendeElselskab,
    "Smart-charge": smartCharge,
    "Solar": Solar,
    "Solcelleaftale": Solcelleaftale,
    "Startside": Startside,
    "Supportkundeportal": Supportkundeportal,
    "TPRS": TPRS,
    "Terms": Terms,
    "Upload": Upload,
    "Virker-enkel-med-alle-ladebokse": virkerEnkelMedAlleLadebokse,
    "Widget": Widget,
    "Zaptec-go-2": zaptecGo2,
    "Zaptec-go": zaptecGo,
}

export const pagesConfig = {
    mainPage: "Forside",
    Pages: PAGES,
    Layout: __Layout,
};